import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUsage } from '@/contexts/UsageContext';
import Pressable from '@/components/base/Pressable';

interface AccountPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function AccountPanel({ open, onClose }: AccountPanelProps) {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { user, isAuthenticated, signOut, updateProfile } = useAuth();
  const { tier } = useUsage();
  const isDark = resolvedTheme === 'dark';

  const displayName = user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'Student';
  const email = user?.email || '';
  const isPremium = tier.tier === 'premium';

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSaved, setNameSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingName && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingName]);

  const startEdit = () => {
    setNameValue(displayName);
    setNameError('');
    setNameSaved(false);
    setEditingName(true);
  };

  const cancelEdit = () => {
    setEditingName(false);
    setNameError('');
  };

  const saveName = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed) { setNameError('Name cannot be empty.'); return; }
    if (trimmed.length > 40) { setNameError('Name must be 40 characters or fewer.'); return; }
    setNameSaving(true);
    setNameError('');
    const { error } = await updateProfile({ display_name: trimmed });
    setNameSaving(false);
    if (error) {
      setNameError(error.message || 'Could not save. Try again.');
    } else {
      setEditingName(false);
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
    }
  };

  const t = {
    backdrop: isDark ? 'bg-black/60' : 'bg-black/30',
    sheetBg: isDark ? 'bg-[#161618]' : 'bg-white',
    sheetBorder: isDark ? 'border-white/[0.05]' : 'border-neutral-200',
    textPrimary: isDark ? 'text-[#F5F5F7]' : 'text-neutral-900',
    textSecondary: isDark ? 'text-[#A1A1A6]' : 'text-neutral-500',
    textMuted: isDark ? 'text-[#6E6E73]' : 'text-neutral-400',
    iconBg: isDark ? 'bg-white/[0.04]' : 'bg-neutral-100',
    cardBg: isDark ? 'bg-white/[0.03]' : 'bg-neutral-50',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-neutral-100',
    divider: isDark ? 'border-white/[0.04]' : 'border-neutral-100',
    closeBtn: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]',
    btnPrimary: isDark ? 'bg-[#F5F5F7] text-[#0F0F10]' : 'bg-neutral-900 text-white',
    btnGhost: isDark ? 'bg-white/[0.04] text-[#F5F5F7] border-white/[0.08]' : 'bg-neutral-50 text-neutral-900 border-neutral-200',
    inputBg: isDark ? 'bg-white/[0.06]' : 'bg-white',
    inputBorder: isDark ? 'border-white/[0.12]' : 'border-neutral-300',
    danger: isDark ? 'text-red-400' : 'text-red-500',
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
    navigate('/');
  };

  const handleForgotPassword = () => {
    onClose();
    navigate('/auth/forgot-password');
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-end lg:items-center lg:justify-end">
          <motion.div
            className={`absolute inset-0 ${t.backdrop}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className={`relative w-full lg:w-[400px] lg:h-full lg:max-h-full ${t.sheetBg} lg:border-l ${t.sheetBorder} overflow-hidden flex flex-col`}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            style={{ maxHeight: '85vh', borderRadius: '24px 24px 0 0' }}
          >
            <div className="flex justify-center pt-3 pb-1 lg:hidden">
              <div className={`w-10 h-1 ${isDark ? 'bg-white/20' : 'bg-black/20'} rounded-full`} />
            </div>

            <div className="flex items-center justify-between px-5 pt-2 pb-3 flex-shrink-0">
              <h3 className={`text-base font-semibold ${t.textPrimary}`}>Account</h3>
              <Pressable onClick={onClose} scale={0.88}>
                <div className={`w-9 h-9 rounded-full ${t.closeBtn} flex items-center justify-center`}>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`ri-close-line ${t.textSecondary} text-sm`} />
                  </div>
                </div>
              </Pressable>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-5 pb-8">
              {isAuthenticated ? (
                <div className="space-y-5">
                  {/* Profile Info Card */}
                  <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-5`}>
                    {/* Avatar + name row */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-full ${t.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <span className={`text-lg font-bold ${t.textPrimary}`}>
                          {displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${t.textPrimary} truncate`}>{displayName}</p>
                        <p className={`text-[11px] ${t.textMuted} truncate`}>{email}</p>
                      </div>
                    </div>

                    <div className={`space-y-4 border-t ${t.divider} pt-4`}>
                      {/* Email row — read-only */}
                      <div>
                        <p className={`text-[10px] font-semibold uppercase tracking-wider ${t.textMuted} mb-1`}>Email</p>
                        <p className={`text-sm ${t.textPrimary}`}>{email}</p>
                      </div>

                      {/* Display name row — editable */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className={`text-[10px] font-semibold uppercase tracking-wider ${t.textMuted}`}>Display Name</p>
                          {!editingName && (
                            <button
                              onClick={startEdit}
                              className={`text-[11px] font-medium ${isDark ? 'text-white/50 hover:text-white/80' : 'text-neutral-400 hover:text-neutral-700'} transition-colors cursor-pointer`}
                            >
                              Edit
                            </button>
                          )}
                        </div>

                        <AnimatePresence mode="wait">
                          {editingName ? (
                            <motion.div
                              key="edit"
                              initial={{ opacity: 0, y: -4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.15 }}
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  ref={inputRef}
                                  type="text"
                                  value={nameValue}
                                  onChange={(e) => setNameValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveName();
                                    if (e.key === 'Escape') cancelEdit();
                                  }}
                                  maxLength={40}
                                  className={`flex-1 text-sm px-3 py-2 rounded-xl border ${t.inputBg} ${t.inputBorder} ${t.textPrimary} outline-none transition-all`}
                                  placeholder="Your display name"
                                />
                                <button
                                  onClick={saveName}
                                  disabled={nameSaving}
                                  className={`px-3 py-2 rounded-xl text-xs font-semibold bg-neutral-900 text-white disabled:opacity-50 cursor-pointer`}
                                >
                                  {nameSaving ? (
                                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  ) : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className={`px-3 py-2 rounded-xl text-xs font-medium ${t.textMuted} ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-neutral-100'} transition-colors cursor-pointer`}
                                >
                                  Cancel
                                </button>
                              </div>
                              {nameError && (
                                <p className="text-[11px] text-red-400 mt-1.5">{nameError}</p>
                              )}
                            </motion.div>
                          ) : (
                            <motion.div
                              key="display"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="flex items-center gap-2"
                            >
                              <p className={`text-sm ${t.textPrimary}`}>{displayName}</p>
                              {nameSaved && (
                                <motion.span
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  exit={{ opacity: 0 }}
                                  className="text-[11px] text-emerald-400 flex items-center gap-1"
                                >
                                  <i className="ri-check-line text-xs" /> Saved
                                </motion.span>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Status */}
                  <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-4`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`ri-vip-crown-line ${t.textMuted} text-xs`} />
                        </div>
                        <span className={`text-sm ${t.textPrimary}`}>Plan</span>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        isPremium
                          ? isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                          : isDark ? 'bg-white/[0.06] text-[#A1A1A6]' : 'bg-neutral-100 text-neutral-500'
                      }`}>
                        {isPremium ? 'Premium' : 'Free'}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <Pressable onClick={handleForgotPassword} scale={0.97}>
                      <div className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border ${t.btnGhost} cursor-pointer`}>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-key-line text-xs" />
                        </div>
                        <span className="text-sm font-medium">Change Password</span>
                      </div>
                    </Pressable>
                    <Pressable onClick={handleSignOut} scale={0.97}>
                      <div className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border ${t.btnGhost} cursor-pointer`}>
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-logout-box-r-line text-xs" />
                        </div>
                        <span className="text-sm font-medium">Sign out</span>
                      </div>
                    </Pressable>
                  </div>
                </div>
              ) : (
                /* Guest / not signed in */
                <div className="space-y-5">
                  <div className="flex flex-col items-center text-center py-8">
                    <div className={`w-16 h-16 rounded-full ${t.iconBg} flex items-center justify-center mb-4`}>
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`ri-user-3-line ${t.textMuted} text-2xl`} />
                      </div>
                    </div>
                    <h4 className={`text-base font-semibold ${t.textPrimary} mb-1`}>No Account Connected</h4>
                    <p className={`text-xs font-semibold ${t.textMuted} mb-2`}>Free plan</p>
                    <p className={`text-sm ${t.textMuted} max-w-[240px] leading-relaxed`}>
                      Sign in to sync your progress, save preferences, and access your content across all devices.
                    </p>
                  </div>

                  <div className="space-y-2.5">
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => { onClose(); navigate('/auth/signup'); }}
                      className={`w-full py-3 rounded-2xl text-sm font-semibold cursor-pointer ${t.btnPrimary}`}
                    >
                      Create Account
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => { onClose(); navigate('/auth/login'); }}
                      className={`w-full py-3 rounded-2xl text-sm font-semibold cursor-pointer border ${t.btnGhost}`}
                    >
                      Log In
                    </motion.button>
                  </div>

                  <div className={`rounded-2xl border ${t.cardBorder} ${t.cardBg} p-4`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`ri-vip-crown-line ${t.textMuted} text-xs`} />
                        </div>
                        <span className={`text-sm ${t.textPrimary}`}>Current Plan</span>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isDark ? 'bg-white/[0.06] text-[#A1A1A6]' : 'bg-neutral-100 text-neutral-500'}`}>
                        Free
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
